import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

import {
    DndContext,
    DragOverlay,
    MouseSensor,
    useDroppable,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import dayjs from 'dayjs';

import useUser from '@/hooks/swr/useUser';
import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';
import { useNewFunnelActivated } from '@/utils/scheduler/funnel/hooks/useNewFunnelActivated';

import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import { formatLibraryMediaForPost } from '@/utils/scheduler/funnel/mediaLibrary/formatLibraryMediaForPost';
import useToggle from '@/hooks/utils/useToggle';

import PropTypes from 'prop-types';

// Lib
import { POST_STATUS } from '@/lib/scheduler/postStatus';

// Components
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import LoadMoreButton from '@/components/composite/Button/LoadMoreButton';
import MobilePreview from '@/components/composite/Scheduler/Preview/MobilePreview';
import { ImageMedia } from '@/components/pages/scheduler/medialibrary/MediaLibraryScheduler';
import { MediaLibraryWrapper } from '@/components/pages/scheduler/medialibrary/MediaLibraryWrapper';

import SchedulerPost from './SchedulerPost';
import { SCHEDULER_PROFILE } from '@/routes/apiEndpoints';

const ID_INVISIBLE_ITEM = 'INVISIBLE_ITEM';

// This wrapper prevents being unable to drop on first position from media library when there are draft posts already scheduled
const WrapperPostedItem = ({ areAnyDraftPosts, post, children }) => {
    if (!post?.id) return null;

    if (areAnyDraftPosts) {
        return children;
    } else {
        return (
            <SortableItem key={post.id.toString()} id={post.id} data={post} disabled>
                {children}
            </SortableItem>
        );
    }
};

WrapperPostedItem.propTypes = {
    areAnyDraftPosts: PropTypes.bool,
    post: PropTypes.object,
    children: PropTypes.node
};

const FeedMobilePreview = ({
    posts,
    isLoading,
    isSaving,
    isLoadMore,
    contentHeight,
    loadMore,
    isLoadingMore,
    activeFilter,
    resetFilters = () => {
        /** */
    },
    openMediaLibrary = false,
    reorderPosts = () => {
        /** */
    },
    setDeleteModal = () => {
        /** */
    },
    onCloseMediaLibrary = () => {
        /** */
    }
}) => {
    const { t } = useTranslation('scheduler');
    const { activeSocialProfile } = useActiveSocialProfile();
    const { isNewFunnel } = useNewFunnelActivated();
    const { user } = useUser();
    const [arrayPosts, setArrayPosts] = useState(posts);
    const [currentOverId, setCurrentOverId] = useState(null);
    const [draggedPost, setDraggedPost] = useState(null);
    const [mediaLibraryState, setMediaLibraryState] = useState({
        activeMedia: null,
        isActive: false,
        keyReloadUniqMediaIds: Date.now()
    });

    const { isOpen: isOpenMediaLibraryError, toggle: toggleMediaLibraryError } = useToggle({
        initial: false
    });
    const { setNodeRef } = useDroppable({
        id: 'droppable'
    });
    const userTimezone = user?.settings?.timezone ?? 'Europe/Paris';

    const itemIds = useMemo(() => {
        if (arrayPosts?.length) {
            return arrayPosts
                .filter((post) => post.scheduled_at)
                ?.map((item) => item?.id?.toString());
        }
        return [];
    }, [arrayPosts]);

    // Dndkit sensors
    const mouseSensor = useSensor(MouseSensor, {
        activationConstraint: {
            distance: 0
        }
    });

    const sensors = useSensors(mouseSensor);

    const scheduleAtRightTime = (index, arrayPosts) => {
        let schedule_time = '';
        const previousPost = arrayPosts[index + 1];
        const nextPost = arrayPosts[index - 1];
        const currentHour = dayjs().utc().tz(userTimezone);

        if (nextPost?.scheduled_at && previousPost?.scheduled_at) {
            // Dropping a media in between two scheduled posts
            const previousPostHour = dayjs(previousPost.scheduled_at).utc().tz(userTimezone);
            const hourDiff = dayjs(nextPost?.scheduled_at).diff(
                previousPostHour.utc().tz(userTimezone),
                'hour'
            );

            if (hourDiff >= 2) {
                // If there are more than 2 hours between the two posts
                schedule_time = dayjs(nextPost.scheduled_at).utc().subtract(1, 'hour').format();
            } else {
                const timeDiff = dayjs(nextPost?.scheduled_at).diff(
                    previousPostHour.utc().tz(userTimezone),
                    'minute'
                );

                schedule_time = previousPostHour
                    .utc()
                    .add(timeDiff / 2, 'minute')
                    .format();
            }
        } else if (previousPost?.scheduled_at) {
            // Dropping a media after a scheduled post
            const previousPostTime = dayjs(previousPost.scheduled_at).utc();
            schedule_time = previousPostTime.add(1, 'minute').format();
        } else if (nextPost?.scheduled_at) {
            // Dropping a media before a scheduled post
            const hourDiff = dayjs(nextPost?.scheduled_at).diff(
                currentHour.utc().tz(userTimezone),
                'hour'
            );

            if (hourDiff >= 2) {
                // If there are more than 2 hours between current time and next post schedule time
                schedule_time = dayjs(nextPost.scheduled_at).utc().subtract(1, 'hour').format();
            } else {
                const timeDiff = dayjs(nextPost?.scheduled_at).diff(
                    currentHour.utc().tz(userTimezone),
                    'minute'
                );
                schedule_time = currentHour
                    .utc()
                    .add(timeDiff / 2, 'minute')
                    .format();
            }
        } else {
            schedule_time = currentHour.utc().add(2, 'hour').set('minute', 0).format();
        }

        return schedule_time;
    };

    // Dndkit events
    const handleDragStart = (event) => {
        if (event.active?.id?.toString().split('-')[0] === 'draggable') {
            let activeMedia = event.active?.data?.current;
            if (isNewFunnel) {
                const media = event.active?.data?.current?.data?.medias?.[0];
                const mediaToFormat = { id: media?.library_media_id, medias: [{ ...media }] };
                const currentData = event.active?.data?.current;
                activeMedia = {
                    ...currentData,
                    data: {
                        ...currentData?.data,
                        medias: [formatLibraryMediaForPost(mediaToFormat)]
                    }
                };
            }
            setMediaLibraryState((prevState) => ({
                ...prevState,
                activeMedia: activeMedia,
                isActive: true
            }));
        } else {
            setDraggedPost(
                arrayPosts.find((post) =>
                    typeof post.id === 'string'
                        ? post.id === event.active.id
                        : post.id === parseInt(event.active.id, 10)
                )
            );
        }
    };

    const onDragOver = (event) => {
        const { active, over } = event;
        if (active?.id?.toString().split('-')[0] === 'draggable' && active) {
            if (over && currentOverId !== over.id) {
                // If the over id is different from the current over id, we add the active media to the array posts
                // This is to avoid glitching when dragging on object. That cause the app to crash due to maximum depth update amount
                // ICO-4026
                setCurrentOverId(over?.id);
                setArrayPosts((prevState) => {
                    const activeIndex = prevState.findIndex((item) => item.id === active.id);
                    if (activeIndex === -1) {
                        return [
                            {
                                ...mediaLibraryState.activeMedia,
                                id: active.id,
                                scheduled_at: 'temporary_schedule'
                            },
                            ...prevState
                        ];
                    } else {
                        return prevState;
                    }
                });
            } else {
                setArrayPosts((prevState) => prevState.filter((item) => item.id !== active.id));
            }
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (
            over &&
            (active.id !== over.id || active.id?.toString().split('-')[0] === 'draggable')
        ) {
            let reorderedPosts = [];

            let activeIndex = arrayPosts.findIndex((post) =>
                typeof post.id === 'string'
                    ? post.id === active.id
                    : post.id === parseInt(active.id, 10)
            );

            let overIndex = arrayPosts.findIndex((post) =>
                typeof post.id === 'string'
                    ? post.id === over.id
                    : post.id === parseInt(over.id, 10)
            );

            if (!over?.data?.current.scheduled_at) {
                // Drop in an empty list - First post scheduled
                reorderedPosts = [
                    {
                        ...arrayPosts[activeIndex],
                        scheduled_at: scheduleAtRightTime(activeIndex, reorderedPosts)
                    },
                    ...arrayPosts.slice(activeIndex + 1, arrayPosts.length)
                ];
            } else {
                if (arrayPosts[activeIndex].scheduled_at === 'temporary_schedule') {
                    // Add dropped post into arrayPosts
                    reorderedPosts = [
                        ...arrayPosts.slice(1, overIndex + 1),
                        active.data.current,
                        ...arrayPosts.slice(overIndex + 1, arrayPosts.length)
                    ];

                    reorderedPosts[overIndex] = {
                        ...reorderedPosts[overIndex],
                        scheduled_at: scheduleAtRightTime(overIndex, reorderedPosts)
                    };
                } else {
                    // Otherwise, we re-order the array
                    reorderedPosts = arrayMove(arrayPosts, activeIndex, overIndex).map(
                        (post, index) => ({
                            ...post,
                            scheduled_at: arrayPosts[index].scheduled_at
                        })
                    );
                }
            }

            reorderPosts(reorderedPosts);
            setArrayPosts(reorderedPosts);
            setCurrentOverId(null);
        }
        setDraggedPost(null);

        if (mediaLibraryState.isActive) {
            setMediaLibraryState((prevState) => ({
                ...prevState,
                isActive: false,
                keyReloadUniqMediaIds: Date.now()
            }));
        }
    };

    useEffect(() => {
        if (!mediaLibraryState.isActive) {
            setArrayPosts(posts);
        }
    }, [mediaLibraryState.isActive, posts]);

    return (
        <>
            <div
                className={clsx('relative flex items-end justify-between lg:pt-8', {
                    'pl-12': openMediaLibrary
                })}>
                <p className="relative mb-5 ml-20 w-1/2 max-w-[12rem] text-xs text-icoBlue">
                    {t('feed.instructions')}
                    <span className="absolute -bottom-1.5 -left-5 inline-block h-3.5 w-3.5 rounded-tl-xl border-l-2 border-t-2 border-icoBlue" />
                </p>
            </div>
            <DndContext
                sensors={sensors}
                onDragOver={onDragOver}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}>
                <div className="flex justify-end gap-x-4 xl:gap-x-12">
                    <MobilePreview
                        wrapperHeight={contentHeight}
                        apiEndpoint={injectEndpointUrlParams(SCHEDULER_PROFILE, {
                            SPID: activeSocialProfile?.id
                        })}
                        socialProfile={activeSocialProfile}>
                        <div
                            className={clsx('relative grid w-full grid-cols-12 gap-0.5', {
                                'animate-pulse': isLoading
                            })}
                            ref={setNodeRef}>
                            {isLoading ? (
                                [...Array(18).keys()].map((index) => (
                                    <div
                                        key={index}
                                        className="col-span-4 aspect-[4/5] bg-icoGray-300 dark:bg-icoDarkMode-deepBlack"
                                    />
                                ))
                            ) : (
                                <SortableContext items={itemIds} strategy={rectSortingStrategy}>
                                    {arrayPosts?.length > 0 &&
                                        itemIds?.length > 0 &&
                                        arrayPosts
                                            ?.filter((post) => {
                                                if (post.scheduled_at) {
                                                    if (
                                                        activeFilter.value !== 'all' &&
                                                        post.id.toString().split('-')[0] ===
                                                            'draggable'
                                                    ) {
                                                        return false;
                                                    }

                                                    return post;
                                                }
                                            })
                                            ?.map((post) => {
                                                if (!post?.id) {
                                                    return null;
                                                }
                                                return (
                                                    <SortableItem
                                                        key={post.id.toString()}
                                                        id={post.id}
                                                        data={post}
                                                        disabled={isSaving}>
                                                        <Item
                                                            post={post}
                                                            disabled={isSaving}
                                                            onRemoveList={(id) => {
                                                                const newArray = arrayPosts.filter(
                                                                    (item) => item.id !== id
                                                                );
                                                                setArrayPosts(newArray);
                                                                reorderPosts(newArray);
                                                            }}
                                                            setDeleteModal={setDeleteModal}
                                                            className={clsx({
                                                                'opacity-50':
                                                                    draggedPost?.id === post.id
                                                            })}
                                                        />
                                                    </SortableItem>
                                                );
                                            })}
                                    {arrayPosts?.length > 0 ? (
                                        itemIds?.length < arrayPosts?.length &&
                                        arrayPosts
                                            ?.filter((post) => !post.scheduled_at)
                                            ?.map((post) => (
                                                <WrapperPostedItem
                                                    key={post.id.toString()}
                                                    post={post}
                                                    areAnyDraftPosts={
                                                        arrayPosts?.filter(
                                                            (post) => post.scheduled_at
                                                        )?.length > 0
                                                    }>
                                                    <Tooltip
                                                        position="top"
                                                        content={t(
                                                            'feed.cant_reorder_published_posts'
                                                        )}>
                                                        <div className="relative col-span-4">
                                                            <Item post={post} disabled />
                                                        </div>
                                                    </Tooltip>
                                                </WrapperPostedItem>
                                            ))
                                    ) : (
                                        <>
                                            {/* Invisible item to be able to drop an item even when the list is empty */}
                                            <SortableItem
                                                id={ID_INVISIBLE_ITEM}
                                                data={null}
                                                disabled
                                                className="invisible opacity-0">
                                                <Item post={null} disabled />
                                            </SortableItem>
                                            <p className="absolute left-1/2 top-6 -translate-x-1/2 font-GMedium text-xs text-icoGray-400 dark:text-icoDarkMode-beton">
                                                {t('feed.empty_list_posts')}
                                            </p>
                                        </>
                                    )}
                                </SortableContext>
                            )}
                            <DragOverlay>
                                {mediaLibraryState.isActive ? (
                                    <div className="draggable-overlay aspect-[4/5]">
                                        <ImageMedia media={mediaLibraryState.activeMedia} />
                                    </div>
                                ) : (
                                    <Item post={draggedPost} dragged={true} />
                                )}
                            </DragOverlay>
                        </div>
                        {isLoadMore ? (
                            <div className="my-2 flex w-full justify-center px-2">
                                <LoadMoreButton
                                    isLoadingMore={isLoadingMore}
                                    loadMoreItems={loadMore}
                                    size="lg"
                                />
                            </div>
                        ) : null}
                    </MobilePreview>
                    {openMediaLibrary ? (
                        <div className="relative h-full w-full max-w-[192px] xl:max-w-[290px]">
                            <div
                                className="absolute left-0 top-0 w-full"
                                style={{ height: `${contentHeight - 99}px` }}>
                                <MediaLibraryWrapper
                                    isFeedPreview
                                    activeFilter={activeFilter}
                                    resetFilters={resetFilters}
                                    mediaLibraryState={mediaLibraryState}
                                    toggleMediaLibraryError={toggleMediaLibraryError}
                                    isOpenMediaLibraryError={isOpenMediaLibraryError}
                                    onClose={onCloseMediaLibrary}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </DndContext>
        </>
    );
};

FeedMobilePreview.propTypes = {
    posts: PropTypes.array,
    isLoading: PropTypes.bool,
    isSaving: PropTypes.bool,
    reorderPosts: PropTypes.func,
    setDeleteModal: PropTypes.func,
    contentHeight: PropTypes.number,
    isLoadMore: PropTypes.bool,
    loadMore: PropTypes.func,
    onCloseMediaLibrary: PropTypes.func,
    isLoadingMore: PropTypes.bool
};

const SortableItem = ({ id, data = {}, disabled = false, children, className = '' }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: id.toString(),
        disabled: disabled,
        data
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    return (
        <div
            className={clsx('group relative col-span-4', className)}
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}>
            {children}
        </div>
    );
};

SortableItem.propTypes = {
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    disabled: PropTypes.bool,
    children: PropTypes.node
};

const buttonRemoveStyle =
    'invisible group-hover:visible absolute top-1 right-1 flex justify-center items-center overflow-hidden bg-icoGray-300 text-icoGray-500 text-xs w-5 h-5 rounded-sm focus:outline-none dark:bg-icoDarkMode-goudron dark:text-icoDarkMode-wolf';

const Item = forwardRef(
    (
        {
            post,
            dragged = false,
            disabled = false,
            setDeleteModal = () => {
                /** */
            },
            onRemoveList = () => {
                /** */
            },
            className
        },
        ref
    ) => {
        const { t } = useTranslation('commons');
        const isDroppedItem = post?.id?.toString().split('-')[0] === 'draggable';

        return (
            <>
                <div
                    ref={ref}
                    className={clsx(
                        'relative aspect-[4/5] w-full overflow-hidden rounded-[1px] bg-icoGray-300 dark:bg-icoDarkMode-deepBlack',
                        {
                            'cursor-default': disabled,
                            'cursor-grab': !disabled,
                            'cursor-grabbing shadow-md': dragged
                        },
                        className
                    )}>
                    {(post?.data?.medias && post?.data?.medias.length) ||
                    (post?.medias && post?.medias.length) ? (
                        <div className="absolute h-full w-full">
                            <SchedulerPost.Img
                                src={
                                    post.scheduled_at
                                        ? post?.data?.medias?.[0]?.min_thumbnail ??
                                          post?.data?.medias?.[0]?.image ??
                                          post?.data?.medias?.[0]?.thumbnail
                                        : post.medias?.[0]?.min_thumbnail ??
                                          post.medias?.[0]?.image ??
                                          post.medias?.[0]?.thumbnail
                                }
                                postId={post.id}
                            />
                        </div>
                    ) : (
                        <span className="flex h-full w-full items-center justify-center bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                            <Icon
                                icon="content"
                                className="text-2xl text-icoGray-500 dark:text-icoDarkMode-wolf"
                            />
                        </span>
                    )}
                </div>
                {post?.state ? (
                    <span
                        className="absolute left-1.5 top-1.5 h-2 w-2 rounded-full border border-white bg-icoAquaBlue dark:border-icoDarkMode-deepBlack"
                        style={{ backgroundColor: POST_STATUS[post.state].color }}
                    />
                ) : null}
                {post?.scheduled_at ? (
                    <button
                        aria-label={t('delete')}
                        type="button"
                        className={buttonRemoveStyle}
                        onClick={() => {
                            if (isDroppedItem) {
                                onRemoveList(post.id);
                            } else {
                                setDeleteModal((prevState) => ({
                                    ...prevState,
                                    post: post,
                                    isOpen: true
                                }));
                            }
                        }}>
                        <Icon icon="trash" />
                        <p className="absolute text-transparent">{t('delete')}</p>
                    </button>
                ) : null}
            </>
        );
    }
);

Item.propTypes = {
    post: PropTypes.object,
    dragged: PropTypes.bool,
    disabled: PropTypes.bool,
    onRemoveList: PropTypes.func,
    setDeleteModal: PropTypes.func,
    className: PropTypes.string
};

export default FeedMobilePreview;
